import React, { useMemo, useState } from "react";
import cwmMascotWhite from "../../assets/brand/cwm-logo-mascot-white.png";
import { GROUP_PALETTE } from "./taxonomy";
import MapboxMap, { mapboxAvailable } from "./MapboxMap";

/**
 * Map view shown when the filter bar's "Map" toggle is active.
 *
 * Uses Mapbox GL when REACT_APP_MAPBOX_TOKEN is set (real tiled map with
 * accurate restaurant locations). Falls back to a stylized illustrative
 * SVG map when the token is missing — so dev/preview without credentials
 * still renders cleanly, and the brand stays on-key.
 */
export default function MapView({ restaurants = [], onChat }) {
  if (mapboxAvailable()) {
    return (
      <MapboxMap restaurants={restaurants} onChat={onChat} PreviewCard={MapPreviewCard} />
    );
  }
  return <StylizedMap restaurants={restaurants} onChat={onChat} />;
}

function StylizedMap({ restaurants = [], onChat }) {
  const [selectedId, setSelectedId] = useState(() => restaurants[0]?.id ?? null);

  // Deterministic pin positions — hash restaurant id into a map coordinate
  // so the same restaurant always lands in the same spot. Points stay
  // clustered roughly mid-map, avoiding the edges (bottom card covers ~35%).
  const pins = useMemo(() => {
    return restaurants.map((r, i) => {
      const seed = typeof r.id === "number" ? r.id : i * 37 + 11;
      const x = 12 + ((seed * 53) % 76);
      const y = 10 + ((seed * 29) % 54);
      const group = inferGroup(r);
      const palette = GROUP_PALETTE[group] || GROUP_PALETTE.Lifestyle;
      return { r, x, y, palette };
    });
  }, [restaurants]);

  const selected = pins.find((p) => p.r.id === selectedId) || pins[0];

  if (!restaurants.length) {
    return (
      <div className="cwm-map-empty">
        <img src={cwmMascotWhite} alt="" style={{ height: 56, opacity: 0.7 }} />
        <div style={{ fontWeight: 800, fontSize: 16, color: "#fff", marginTop: 10 }}>No restaurants to plot</div>
        <div style={{ fontSize: 13, color: "rgba(255,255,255,0.8)", marginTop: 4, textAlign: "center", maxWidth: 300 }}>
          Tweak your filters or switch back to list view.
        </div>
      </div>
    );
  }

  return (
    <div className="cwm-map-wrap">
      <div className="cwm-map-canvas">
        <MapBackdrop />

        {/* Pins */}
        {pins.map(({ r, x, y, palette }) => {
          const isSelected = selected && r.id === selected.r.id;
          return (
            <button
              key={r.id}
              type="button"
              className={`cwm-map-pin ${isSelected ? "is-selected" : ""}`}
              style={{
                left: `${x}%`,
                top: `${y}%`,
                "--pin-color": palette.active,
                "--pin-ring": `${palette.active}55`,
              }}
              onClick={() => setSelectedId(r.id)}
              aria-label={r.name}
            >
              <span className="cwm-map-pin-dot">
                <img src={cwmMascotWhite} alt="" style={{ height: 20, width: "auto" }} />
              </span>
              <span className="cwm-map-pin-label">{r.name}</span>
            </button>
          );
        })}

        {/* Map controls (top-right) */}
        <div className="cwm-map-controls">
          <button type="button" title="Zoom in">+</button>
          <button type="button" title="Zoom out">−</button>
          <button type="button" title="Re-center">
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="8"/>
              <line x1="12" y1="2" x2="12" y2="6"/>
              <line x1="12" y1="18" x2="12" y2="22"/>
              <line x1="2" y1="12" x2="6" y2="12"/>
              <line x1="18" y1="12" x2="22" y2="12"/>
            </svg>
          </button>
        </div>

        {/* Legend */}
        <div className="cwm-map-legend">
          <div className="cwm-map-legend-dot" style={{ background: "#1E9BFF" }}/>
          <span>{restaurants.length} places on this map</span>
        </div>
      </div>

      {/* Selected card */}
      {selected && (
        <MapPreviewCard
          r={selected.r}
          accent={selected.palette.active}
          onChat={onChat}
        />
      )}
    </div>
  );
}

function MapBackdrop() {
  return (
    <svg
      className="cwm-map-svg"
      viewBox="0 0 1000 600"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <pattern id="cwm-map-grid" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
          <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#DCE8F5" strokeWidth="1"/>
        </pattern>
        <linearGradient id="cwm-map-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#F4F8FE"/>
          <stop offset="100%" stopColor="#E3EEFB"/>
        </linearGradient>
        <linearGradient id="cwm-map-road" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.3"/>
          <stop offset="40%" stopColor="#fff" stopOpacity="0.95"/>
          <stop offset="60%" stopColor="#fff" stopOpacity="0.95"/>
          <stop offset="100%" stopColor="#fff" stopOpacity="0.3"/>
        </linearGradient>
      </defs>

      <rect x="0" y="0" width="1000" height="600" fill="url(#cwm-map-bg)"/>
      <rect x="0" y="0" width="1000" height="600" fill="url(#cwm-map-grid)"/>

      {/* Water / park blobs */}
      <path d="M 720 40 Q 820 60 860 140 Q 880 220 780 260 Q 700 280 680 200 Q 660 100 720 40 Z"
            fill="#B7DDFB" opacity="0.8"/>
      <path d="M 40 420 Q 120 420 150 470 Q 170 530 100 540 Q 30 540 20 490 Q 10 440 40 420 Z"
            fill="#C8E6C9" opacity="0.7"/>
      <path d="M 430 100 Q 520 80 560 140 Q 560 200 480 210 Q 410 200 410 150 Q 410 110 430 100 Z"
            fill="#C8E6C9" opacity="0.55"/>

      {/* Main avenues — thick white with soft halo */}
      <g strokeLinecap="round">
        <line x1="0" y1="300" x2="1000" y2="300" stroke="url(#cwm-map-road)" strokeWidth="16"/>
        <line x1="0" y1="300" x2="1000" y2="300" stroke="#fff" strokeWidth="8" strokeDasharray="8 8" opacity="0.6"/>

        <line x1="500" y1="0" x2="500" y2="600" stroke="url(#cwm-map-road)" strokeWidth="14"/>
        <line x1="500" y1="0" x2="500" y2="600" stroke="#fff" strokeWidth="6" opacity="0.9"/>

        <line x1="0" y1="150" x2="1000" y2="150" stroke="#fff" strokeWidth="5" opacity="0.8"/>
        <line x1="0" y1="450" x2="1000" y2="450" stroke="#fff" strokeWidth="5" opacity="0.8"/>

        <line x1="250" y1="0" x2="250" y2="600" stroke="#fff" strokeWidth="4" opacity="0.75"/>
        <line x1="750" y1="0" x2="750" y2="600" stroke="#fff" strokeWidth="4" opacity="0.75"/>

        {/* Diagonal "broadway" */}
        <line x1="150" y1="600" x2="900" y2="60" stroke="#fff" strokeWidth="6" opacity="0.75"/>
      </g>

      {/* City blocks — soft rounded rectangles */}
      <g fill="#F8FAFD" stroke="#E4ECF6" strokeWidth="1">
        <rect x="70" y="40" width="150" height="90" rx="8"/>
        <rect x="280" y="40" width="180" height="90" rx="8"/>
        <rect x="540" y="180" width="170" height="100" rx="8"/>
        <rect x="70" y="180" width="150" height="100" rx="8"/>
        <rect x="280" y="180" width="180" height="100" rx="8"/>
        <rect x="770" y="340" width="150" height="90" rx="8"/>
        <rect x="540" y="340" width="180" height="90" rx="8"/>
        <rect x="280" y="340" width="210" height="90" rx="8"/>
        <rect x="70" y="340" width="150" height="60" rx="8"/>
      </g>

      {/* Neighborhood labels */}
      <g fontFamily="Nunito, system-ui" fontWeight="900" fill="#6B7A90" textAnchor="middle" opacity="0.6">
        <text x="150" y="90" fontSize="14" letterSpacing="3">WEST VILLAGE</text>
        <text x="380" y="90" fontSize="14" letterSpacing="3">SOHO</text>
        <text x="820" y="130" fontSize="14" letterSpacing="3" fill="#4A79B5" opacity="0.7">EAST RIVER</text>
        <text x="380" y="390" fontSize="14" letterSpacing="3">LOWER EAST</text>
        <text x="840" y="390" fontSize="14" letterSpacing="3">TWO BRIDGES</text>
        <text x="150" y="500" fontSize="14" letterSpacing="3" fill="#5E8F4F" opacity="0.75">HUDSON RIVER PARK</text>
      </g>
    </svg>
  );
}

function MapPreviewCard({ r, accent, onChat }) {
  const rating = r.display_rating || r.rating || 4.6;
  const reviews = r.display_review_count || 120;
  const city = r.city || "New York";
  const cuisine = r.cuisine_type || r.cuisine || "";
  const tags = (r.dietary_display_tags || r.safe?.map((s) => s.label) || []).slice(0, 3);
  const photo = r.photo_url || r.photo;

  return (
    <div className="cwm-map-card" style={{ "--accent": accent }}>
      <div
        className="cwm-map-card-photo"
        style={{ backgroundImage: photo ? `url(${photo})` : undefined }}
      >
        {!photo && <img src={cwmMascotWhite} alt="" style={{ height: 44, opacity: 0.85 }} />}
      </div>
      <div className="cwm-map-card-body">
        <div className="cwm-map-card-eyebrow">{(cuisine || "Restaurant").toUpperCase()}</div>
        <div className="cwm-map-card-name">{r.name}</div>
        <div className="cwm-map-card-meta">
          <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="#0F7FE8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <span>{city}</span>
          <span className="cwm-map-card-dot">·</span>
          <span style={{ color: "#F5A623", letterSpacing: -1 }}>★</span>
          <strong>{rating}</strong>
          <span className="cwm-map-card-meta-subtle">({reviews})</span>
        </div>
        <div className="cwm-map-card-tags">
          {tags.map((t) => (
            <span key={t} className="cwm-map-card-tag">{t}</span>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="cwm-map-card-cta"
        onClick={() => onChat?.(r)}
      >
        Chat With Menu
      </button>
    </div>
  );
}

// Best-effort mapping from restaurant to a tag group for pin color.
function inferGroup(r) {
  const tags = new Set((r.dietary_display_tags || []).concat(r.safe?.map((s) => s.label) || []).map((x) => (x || "").toLowerCase()));
  if ([...tags].some((t) => /vegan|vegetarian|pescat|plant/.test(t))) return "Diet";
  if ([...tags].some((t) => /halal|kosher|lenten|jain|buddh/.test(t))) return "Religious";
  if ([...tags].some((t) => /gluten|dairy|nut|peanut|shell|sesame|allergen/.test(t))) return "Allergies";
  if ([...tags].some((t) => /low-sodium|low-glyc|fodmap|gerd|ibs|crohn/.test(t))) return "Therapeutic";
  return "Lifestyle";
}
